// src/pages/ForgotPass.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaEnvelope, FaArrowLeft, FaCheckCircle, FaExclamationCircle, FaLock } from 'react-icons/fa';
import { forgotPassword, resetPassword, validateResetToken } from '../api/api';
import './ForgotPass.css';

const ForgotPass = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: check email, 3: reset password
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tokenData, setTokenData] = useState(null);

  // Extract token from URL if on reset page
  React.useEffect(() => {
    const path = window.location.pathname;
    const match = path.match(/\/reset-password\/([^/]+)\/([^/]+)\//);
    
    if (match) {
      const uid = match[1];
      const token = match[2];
      setTokenData({ uid, token });
      setStep(3);
      
      // Validate token
      validateToken(uid, token);
    }
  }, []);

  const validateToken = async (uid, token) => {
    setIsLoading(true);
    try {
      await validateResetToken(uid, token);
      setError('');
    } catch (err) {
      setError('This password reset link has expired or is invalid. Please request a new one.');
      setStep(1);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await forgotPassword(email);
      setStep(2);
      setSuccess('Password reset instructions have been sent to your email.');
    } catch (err) {
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      await resetPassword({
        uid: tokenData.uid,
        token: tokenData.token,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      
      setSuccess('Password reset successful! Redirecting to login...');
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="forgot-password-container">
      <div className="forgot-password-background"></div>
      
      <div className="forgot-password-card">
        {/* Header */}
        <div className="forgot-password-header">
          <button onClick={handleBackToLogin} className="forgot-password-back-btn">
            <FaArrowLeft /> Back to Login
          </button>
          <h1 className="forgot-password-title">Reset Your Password</h1>
        </div>

        {/* Error Message */}
        {error && (
          <div className="forgot-password-error">
            <FaExclamationCircle className="error-icon" />
            <span>{error}</span>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="forgot-password-success">
            <FaCheckCircle className="success-icon" />
            <span>{success}</span>
          </div>
        )}

        {/* Step 1: Email Form */}
        {step === 1 && (
          <form onSubmit={handleEmailSubmit} className="forgot-password-form">
            <p className="forgot-password-description">
              Enter your email address and we'll send you instructions to reset your password.
            </p>

            <div className="forgot-password-input-group">
              <label htmlFor="email">
                <FaEnvelope className="input-icon" />
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                disabled={isLoading}
              />
            </div>

            <button 
              type="submit" 
              className="forgot-password-submit-btn"
              disabled={isLoading || !email}
            >
              {isLoading ? (
                <>
                  <span className="spinner"></span>
                  Sending...
                </>
              ) : (
                'Send Reset Instructions'
              )}
            </button>
          </form>
        )}

        {/* Step 2: Check Email */}
        {step === 2 && (
          <div className="forgot-password-check-email">
            <div className="check-email-icon">
              <FaEnvelope />
            </div>
            <h2>Check Your Email</h2>
            <p>
              We've sent password reset instructions to <strong>{email}</strong>
            </p>
            <div className="check-email-instructions">
              <p>📧 Open the email we sent</p>
              <p>🔗 Click the reset password link</p>
              <p>🔐 Create a new password</p>
            </div>
            <p className="check-email-note">
              Didn't receive the email? Check your spam folder or 
              <button 
                onClick={() => setStep(1)} 
                className="resend-link"
                disabled={isLoading}
              >
                try again
              </button>
            </p>
          </div>
        )}

        {/* Step 3: Reset Password Form */}
        {step === 3 && (
          <form onSubmit={handlePasswordReset} className="forgot-password-form">
            <p className="forgot-password-description">
              Create a new password for your account.
            </p>

            <div className="forgot-password-input-group">
              <label htmlFor="new-password">
                <FaLock className="input-icon" />
                New Password
              </label>
              <input
                type="password"
                id="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
                disabled={isLoading}
                minLength="8"
              />
              <small className="password-hint">Must be at least 8 characters</small>
            </div>

            <div className="forgot-password-input-group">
              <label htmlFor="confirm-password">
                <FaLock className="input-icon" />
                Confirm Password
              </label>
              <input
                type="password"
                id="confirm-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                disabled={isLoading}
              />
            </div>

            <button 
              type="submit" 
              className="forgot-password-submit-btn"
              disabled={isLoading || !newPassword || !confirmPassword}
            >
              {isLoading ? (
                <>
                  <span className="spinner"></span>
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>
        )}

        {/* Security Notice */}
        <div className="forgot-password-security">
          <FaLock className="security-icon" />
          <p>
            <strong>Secure Password Reset</strong><br />
            Your password reset link is encrypted and expires in 24 hours.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPass;